import clsx from 'clsx';
import { TokenIcons } from '../../../../assets/icons';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import usePoolState from '../../../../context/pool/usePoolState';
import {
    formatAmountWithSuffix,
    validateErrors,
    verifyChainConfig,
} from '../../../../helpers';
import NoLockWarning from './NoLockWarning';
import useApproveToken from '../../../../hooks/web3/useApproveToken';
import { config } from '../../../../config/config';
import { useWeb3React } from '@web3-react/core';
import {
    CHAIN_INFO,
    EnabledChains,
    INSUFFICIENT_BALANCE,
    TRANSACTION_PENDING,
    Validators,
} from '../../../../config/constants';
import useErrorState from '../../../../context/error/useErrorState';
import useModalState from '../../../../context/modal/useModalState';
import { toBigNumber } from '../../../../web3/utils';
import { parseUnits } from 'ethers/lib/utils';
import useUserBalance from '../../../../hooks/web3/useUserBalance';
import useToastState from '../../../../context/toast/useToastState';
import { ILiquidityPool__factory } from '../../../../config/contracts/typechain';
import { useSWRConfig } from 'swr';
import useTokenDetails from '../../../../hooks/web3/useTokenDetails';
// import useUserLockUps from '../../../../hooks/web3/pools/useUserLockUps';
import useMockUserLockUps from '../../../../hooks/web3/pools/useMockUserLockUps';
// import useUserLockedDeposits from '../../../../hooks/web3/pools/useUserLockedDeposits';
import useMockUserLockedDeposits from '../../../../hooks/web3/pools/useMockUserLockedDeposits';
import PoolExposure from './PoolExposure';
// import useUserTotalDeposits from '../../../../hooks/web3/pools/useUserTotalDeposits';
import useMockUserTotalDeposits from '../../../../hooks/web3/pools/useMockUserTotalDeposits';
import useUserPoolPnl from '../../../../hooks/web3/pools/useUserPoolPnl';
import Spinner from '../../../../components/Spinner';

const PoolOverview = () => {
    const {
        depositAmount,
        selectedLiquidityPool,
        selectedLockUp,
        renderDepositInterface,
        renderExposure,
        toggleExposure,
        toggleDepositInterface,
    } = usePoolState();

    const { openModal, setModalData, closeModal } = useModalState();

    const { setError } = useErrorState();

    const { setToast } = useToastState();

    // const { updateUserLockUps } = useUserLockUps();
    const { updateUserLockUps } = useMockUserLockUps();
    // const { updateUserLockedDeposits } = useUserLockedDeposits();
    const { updateUserLockedDeposits } = useMockUserLockedDeposits();
    // const { updateUserTotalDeposits } = useUserTotalDeposits();
    const { updateUserTotalDeposits } = useMockUserTotalDeposits();
    const { updateUserPoolPnl } = useUserPoolPnl();

    const { chainId, library, account } = useWeb3React();

    const { mutate: globalMutate } = useSWRConfig();

    const userBalance = useUserBalance(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { isApproved, approve } = useApproveToken(
        config.addresses[chainId as EnabledChains[number]]?.collateral,
        selectedLiquidityPool?.poolId,
        depositAmount
    );

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const validators: Validators[] = [
        {
            errorCondition: toBigNumber(depositAmount || '0').isZero(),
            message: 'Amount is required!',
        },
        {
            errorCondition: parseUnits(depositAmount || '0', decimals).gt(
                userBalance || '0'
            ),
            message: INSUFFICIENT_BALANCE,
        },
    ];

    const feeMultiplier =
        selectedLockUp.feeMultiplier === undefined ? 1 : selectedLockUp.feeMultiplier;

    const onConfirm = async () => {
        try {
            if (!selectedLiquidityPool) throw new Error('No selected locked pool');
            if (!verifyChainConfig(chainId)) return;
            if (
                !validateErrors(validators, (message) =>
                    setToast(
                        {
                            type: 'danger',
                            message,
                        },
                        message
                    )
                )
            ) {
                return;
            }

            openModal('waitingModal');
            setModalData((prev) => ({ ...prev, status: TRANSACTION_PENDING }));

            const lpContract = ILiquidityPool__factory.connect(
                selectedLiquidityPool.poolId,
                library.getSigner()
            );

            const deposit = await (selectedLockUp.lockUpId !== undefined
                ? lpContract.depositAndLock(
                      parseUnits(depositAmount, decimals),
                      0,
                      selectedLockUp.lockUpId
                  )
                : lpContract.deposit(parseUnits(depositAmount, decimals), 0));

            const receipt = await deposit.wait();

            // attempt to update chart after 3 seconds
            setTimeout(() => {
                globalMutate([
                    'historicalLockedPoolLiquidity',
                    chainId,
                    selectedLiquidityPool.poolId,
                ]);
            }, 3000);

            await updateUserLockUps();
            await updateUserLockedDeposits();
            await updateUserTotalDeposits();
            await updateUserPoolPnl();

            setToast(
                {
                    type: 'success',
                    message: (
                        <>
                            USDC Deposited!&nbsp;
                            <Button
                                variant='transparent'
                                externalLink={`${
                                    CHAIN_INFO[chainId as EnabledChains[number]]
                                        ?.blockExplorer
                                }/tx/${receipt.transactionHash}`}
                            >
                                View TX
                            </Button>
                        </>
                    ),
                },
                `${selectedLiquidityPool.poolId}-${depositAmount}`
            );

            toggleDepositInterface();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return selectedLiquidityPool ? (
        <Card className='pool-overview'>
            {renderExposure ? (
                <PoolExposure />
            ) : (
                <>
                    {renderDepositInterface && selectedLockUp.lockUpId === undefined ? (
                        <NoLockWarning />
                    ) : (
                        <div className='overview'>
                            <div className='inner__left'>
                                <div className='pool-name'>
                                    {TokenIcons[selectedLiquidityPool.lpToken]}
                                    {selectedLiquidityPool.name}
                                    <Button onClick={toggleExposure}>Exposure</Button>
                                </div>
                                <div className='max-apr'>
                                    {formatAmountWithSuffix(
                                        +selectedLiquidityPool.apr * feeMultiplier,
                                        {
                                            maxDecimals: 2,
                                            minValue: 999_999.99,
                                        }
                                    )}
                                    % APR
                                </div>
                            </div>
                            <div className='divider' />
                            <div className='inner__right'>
                                <div
                                    className={clsx('detail', {
                                        increased: +selectedLiquidityPool.pnl > 0,
                                        decreased: +selectedLiquidityPool.pnl < 0,
                                    })}
                                >
                                    <div className='title'>Total PnL</div>
                                    <div className='value'>
                                        {+selectedLiquidityPool.pnl > 0 ? '+' : ''}
                                        {formatAmountWithSuffix(
                                            selectedLiquidityPool.pnl,
                                            {
                                                minDecimals: 2,
                                                currency: 'USD',
                                                minValue: 999_999_999.99,
                                            }
                                        )}
                                    </div>
                                </div>
                                <div className='detail'>
                                    <div className='title'>Total Fees</div>
                                    <div className='value'>
                                        {formatAmountWithSuffix(
                                            selectedLiquidityPool.feesEarned,
                                            {
                                                minDecimals: 2,
                                                currency: 'USD',
                                                minValue: 999_999_999.99,
                                            }
                                        )}
                                    </div>
                                </div>
                                {/* <div className='detail'>
                                    <div className='title'>Open Interest</div>
                                    <div className='value'>
                                        {formatAmountWithSuffix(
                                            selectedLiquidityPool.openInterest,
                                            {
                                                minDecimals: 2,
                                                currency: 'USD',
                                                minValue: 999_999_999.99,
                                            }
                                        )}
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    )}
                    <div className='buttons-container'>
                        {renderDepositInterface ? (
                            <>
                                {isApproved ? (
                                    <Button onClick={onConfirm}>
                                        Confirm
                                        <span>&nbsp;USDC Deposit</span>
                                    </Button>
                                ) : (
                                    <Button onClick={approve}>Approve</Button>
                                )}
                                <Button
                                    variant='secondary'
                                    onClick={toggleDepositInterface}
                                >
                                    Back
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={
                                    account
                                        ? toggleDepositInterface
                                        : () => openModal('connectWalletModal')
                                }
                            >
                                {account ? 'Deposit USDC' : 'Connect Wallet'}
                            </Button>
                        )}
                    </div>
                </>
            )}
        </Card>
    ) : (
        <Card className='pool-overview'>
            <div className='loading-container'>
                <Spinner />
            </div>
        </Card>
    );
};

export default PoolOverview;
